import { useId, useMemo, useState } from 'react';
import { Combobox } from '@base-ui/react/combobox';
import { Check, ChevronDown, Search, UserRoundX } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { Avatar } from '../avatar/index.ts';

/** The value of the "not listed" row, never the id of a person. */
export const PERSON_SELECT_NOT_LISTED = 'not-listed';

export interface PersonSelectOption {
  id: string;
  name: string;
  /** What the avatar colour is hashed from; the address, so a renamed person keeps their colour. */
  seed?: string;
  imageUrl?: string | null;
  /** One quiet line under the name. */
  description?: string;
}

export interface PersonSelectLabels {
  /** On the field while nothing is chosen. */
  placeholder: string;
  /** In the search box at the top of the list, and its accessible name. */
  search: string;
  /** The search found nobody. */
  empty: (query: string) => string;
  /** The last row, for an answer that is nobody on the list. */
  notListed: string;
}

export interface PersonSelectProps {
  people: readonly PersonSelectOption[];
  /** A person's id, `PERSON_SELECT_NOT_LISTED`, or null while nothing is chosen. */
  value: string | null;
  onValueChange: (value: string | null) => void;
  labels: PersonSelectLabels;
  /** Names the field where no visible label does. */
  'aria-label'?: string;
  /** Points the field at the lines that explain it. */
  'aria-describedby'?: string;
  disabled?: boolean;
  className?: string;
}

interface Row {
  id: string;
  name: string;
  person: PersonSelectOption | null;
}

/* Accents are folded away, so "jose" finds "José". Plain substring, never fuzzy. */
function searchKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Pick one person from a short list you already hold: a button showing who is
 * chosen, opening a list with a search box on top and a fixed last row for
 * "not on this list".
 *
 * Unlike `SearchSelect` nothing is fetched, and unlike `Combobox` free text is
 * no answer. The last row stays put whatever is typed: somebody who cannot
 * find the person has to be able to say so without clearing the search.
 */
export function PersonSelect({
  people,
  value,
  onValueChange,
  labels,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  disabled = false,
  className,
}: PersonSelectProps) {
  const [query, setQuery] = useState('');
  const triggerId = useId();

  const notListed: Row = useMemo(
    () => ({ id: PERSON_SELECT_NOT_LISTED, name: labels.notListed, person: null }),
    [labels.notListed],
  );
  const rows: Row[] = useMemo(
    () => people.map((person) => ({ id: person.id, name: person.name, person })),
    [people],
  );
  const needle = searchKey(query);
  const matches = rows.filter((row) => searchKey(row.name).includes(needle));

  const selected = [...rows, notListed].find((row) => row.id === value) ?? null;

  return (
    <Combobox.Root<Row>
      items={[...rows, notListed]}
      filteredItems={[...matches, notListed]}
      value={selected}
      onValueChange={(next) => onValueChange(next?.id ?? null)}
      onInputValueChange={(next) => setQuery(next)}
      onOpenChange={(open) => !open && setQuery('')}
      itemToStringLabel={(row) => row.name}
      isItemEqualToValue={(a, b) => a.id === b.id}
      disabled={disabled}
    >
      <Combobox.Trigger
        id={triggerId}
        data-slot='person-select'
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        className={cn(
          'flex min-h-12 w-full cursor-pointer items-center gap-2.5 rounded-md border border-transparent bg-fill-faint px-3.25 py-2 text-left transition',
          'hover:bg-fill data-popup-open:bg-fill data-popup-open:shadow-focus',
          /* The same quiet ring as a text field, and only for keyboard focus. */
          'outline-none focus-visible:shadow-focus',
          'disabled:pointer-events-none disabled:opacity-40',
          className,
        )}
      >
        {selected ? (
          <RowContent row={selected} />
        ) : (
          <span className='min-w-0 flex-1 truncate text-base font-medium text-ink-muted'>
            {labels.placeholder}
          </span>
        )}
        <ChevronDown
          className='size-4 shrink-0 stroke-[2.5] text-ink'
          aria-hidden='true'
        />
      </Combobox.Trigger>

      <Combobox.Portal>
        <Combobox.Positioner side='bottom' align='start' sideOffset={6} className='z-50'>
          <Combobox.Popup
            data-slot='person-select-list'
            aria-labelledby={triggerId}
            className='w-(--anchor-width) max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-line bg-surface shadow-menu outline-none animate-pop'
          >
            <div className='border-b border-fill p-1.5'>
              <label className='flex items-center gap-2 rounded-sm bg-fill-faint px-2.75 py-2'>
                <Search
                  className='size-3.75 shrink-0 text-ink-muted'
                  aria-hidden='true'
                />
                <Combobox.Input
                  aria-label={labels.search}
                  placeholder={labels.search}
                  className='w-full border-none bg-transparent text-sm font-medium text-ink outline-none placeholder:text-ink-muted'
                />
              </label>
            </div>

            {/* Base UI makes the popup a dialog around a listbox; both are named after the field. */}
            <Combobox.List aria-labelledby={triggerId}>
              <div className='max-h-56 overflow-y-auto overscroll-contain py-1'>
                {matches.map((row) => (
                  <Combobox.Item
                    key={row.id}
                    value={row}
                    data-slot='person-select-option'
                    className='flex cursor-pointer items-center gap-2.5 px-3.5 py-2 outline-none transition-colors data-highlighted:bg-fill'
                  >
                    <RowContent row={row} />
                    <Combobox.ItemIndicator>
                      <Check
                        className='size-3.75 shrink-0 stroke-[2.5] text-ink'
                        aria-hidden='true'
                      />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                ))}
                {matches.length === 0 && (
                  <p className='px-3.5 py-3 text-sm text-ink-muted'>
                    {labels.empty(query)}
                  </p>
                )}
              </div>
              <Combobox.Item
                value={notListed}
                data-slot='person-select-not-listed'
                className='flex cursor-pointer items-center gap-2.5 border-t border-fill px-3.5 py-2.5 outline-none transition-colors data-highlighted:bg-fill'
              >
                <RowContent row={notListed} />
                <Combobox.ItemIndicator>
                  <Check
                    className='size-3.75 shrink-0 stroke-[2.5] text-ink'
                    aria-hidden='true'
                  />
                </Combobox.ItemIndicator>
              </Combobox.Item>
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
}

/* A person with their disc, name and quiet line; or the dashed disc of "not listed". */
function RowContent({ row }: { row: Row }) {
  if (!row.person) {
    return (
      <>
        <span className='flex size-8 shrink-0 items-center justify-center rounded-full border-[1.5px] border-dashed border-ink-faint'>
          <UserRoundX className='size-3.75 text-ink-muted' aria-hidden='true' />
        </span>
        <span className='min-w-0 flex-1 truncate text-sm font-semibold text-ink'>
          {row.name}
        </span>
      </>
    );
  }

  const { person } = row;
  return (
    <>
      <Avatar name={person.name} seed={person.seed} src={person.imageUrl} size='sm' />
      <span className='flex min-w-0 flex-1 flex-col'>
        <span className='truncate text-sm font-semibold text-ink'>{person.name}</span>
        {person.description && (
          <span className='truncate text-xs text-ink-muted'>{person.description}</span>
        )}
      </span>
    </>
  );
}
